
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Globe, Monitor, Smartphone, Share2 } from 'lucide-react';
import { PainelStatCard } from '@/components/painel/PainelStatCard';
import { cn } from '@/lib/utils';
import { useAnalyticsTraffic } from '@/hooks/use-analytics-traffic';
import { AnalyticsPageChrome, AnalyticsPanel, AnalyticsPeriodBadge } from '@/components/painel/analytics/AnalyticsPageChrome';
import { usePainelText } from '@/components/painel/painel-texts-context';

function formatInt(n: number) {
  return new Intl.NumberFormat('pt-BR').format(n);
}

export function PainelAnalyticsTrafficPage() {
  const pageText = usePainelText('analytics_trafego');
  const { data } = useAnalyticsTraffic();
  const visitsSeries = data.visitsSeries;
  const sources = data.sources;
  const devices = data.devices;

  return (
    <AnalyticsPageChrome
      title={pageText.title}
      description={pageText.description}
      actions={<AnalyticsPeriodBadge>Últimos 7 dias</AnalyticsPeriodBadge>}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <PainelStatCard
          title="Visualizações de página"
          value={formatInt(data.pageviews)}
          icon={Globe}
          trend={data.pageviewsTrend ?? undefined}
          variant="sky"
        />
        <PainelStatCard
          title="Taxa de retorno"
          value={data.returnRateLabel}
          icon={Share2}
          description="Visitantes recorrentes"
          trend={data.returnTrend ?? undefined}
          variant="cyan"
        />
        <PainelStatCard
          title="Desktop"
          value={`${data.desktopPct}%`}
          icon={Monitor}
          variant="indigo"
        />
        <PainelStatCard
          title="Mobile"
          value={`${data.mobilePct}%`}
          icon={Smartphone}
          variant="violet"
        />
      </div>

      <AnalyticsPanel
        title="Volume diário"
        subtitle="Acessos agregados (barras)"
      >
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={visitsSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.5} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="day"
                tick={{ fill: 'rgba(191,219,254,0.5)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'rgba(191,219,254,0.4)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${Math.round(v / 1000)}k`}
              />
              <Tooltip
                contentStyle={{
                  background: 'rgba(15,23,42,0.96)',
                  border: '1px solid rgba(56,189,248,0.28)',
                  borderRadius: 10,
                  fontSize: 12,
                }}
                cursor={{ fill: 'rgba(56,189,248,0.08)' }}
                formatter={(value: number) => [formatInt(value), 'Visitas']}
              />
              <Bar dataKey="visits" fill="url(#barGrad)" radius={[6, 6, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </AnalyticsPanel>

      <div className="grid gap-4 lg:grid-cols-2">
        <AnalyticsPanel title="Origem de referência" subtitle="Onde as sessões começaram (estimado)">
          <ul className="space-y-4">
            {sources.map((s) => (
              <li key={s.label} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <span className="shrink-0 font-inter text-xs text-slate-400 sm:w-44">{s.label}</span>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-white/[0.08]">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-sky-500 to-indigo-500"
                      style={{ width: `${s.pct}%` }}
                    />
                  </div>
                  <div className="flex w-24 shrink-0 items-center justify-end gap-2">
                    <span className="font-poppins text-sm font-semibold tabular-nums text-white">{s.pct}%</span>
                    <span
                      className={cn(
                        'font-poppins text-[11px]',
                        s.delta > 0 ? 'text-emerald-300/90' : s.delta < 0 ? 'text-rose-300/80' : 'text-slate-400/70'
                      )}
                    >
                      {s.delta > 0 ? `▲${s.delta}` : s.delta < 0 ? `▼${Math.abs(s.delta)}` : '—'}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </AnalyticsPanel>

        <AnalyticsPanel title="Dispositivos" subtitle="Distribuição de sessões">
          <div className="space-y-4">
            {devices.map((d) => (
              <div key={d.device} className="flex items-center justify-between gap-4">
                <span className="font-poppins text-sm text-blue-100/90">{d.device}</span>
                <div className="flex flex-1 items-center gap-3">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/[0.08]">
                    <div
                      className="h-full rounded-full bg-cyan-400/70"
                      style={{ width: `${d.v}%` }}
                    />
                  </div>
                  <span className="w-10 text-right font-poppins text-sm font-semibold tabular-nums text-white">
                    {d.v}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </AnalyticsPanel>
      </div>
    </AnalyticsPageChrome>
  );
}
